'use client'

import { Box, Flex, Text, Title } from '@mantine/core'
import { motion } from 'framer-motion'
import { MotionBox } from '@/components/shared/AnimatedComponents'
import { textFadeInVariantsNoTransition, imageScaleVariants, staggerContainer } from '@/lib/animations'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { RefreshCtaButton } from '@/components/Refresh/CategoryPages/RefreshCtaButton'
import type { SkuComponentConfigMap } from '@/types/enums/sku-component-types'

const DEFAULT_BLOG_CTA_LABEL = 'Read the article'

export function SkuBlogRoll({ config }: { config: SkuComponentConfigMap['skuBlogRoll'] }) {
    const { isSm } = useBreakpoint()
    const { title, description, post, eyebrow, ctaLabel = DEFAULT_BLOG_CTA_LABEL, ctaHref } = config

    return (
        <Box
            id="skuBlogRoll"
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px 0px' }}
            variants={staggerContainer}
            p={{ base: 24, sm: 48 }}
            maw={1440}
            mx="auto"
            w="100%"
            bg="rgba(43, 42, 42, 0.08)"
            miw={0}
            style={{ borderRadius: 24 }}
        >
            <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: 24, sm: 60 }} align="stretch" miw={0}>
                <Flex
                    direction="column"
                    gap={24}
                    justify="center"
                    flex={{ base: 'none', md: '0 0 450px' }}
                    w={{ base: '100%', md: undefined }}
                    miw={0}
                >
                    {eyebrow && (
                        <MotionBox variants={textFadeInVariantsNoTransition}>
                            <Text m={0} fw={600} fz={14} lh="130%" c="gray.6" tt="uppercase" lts={1}>
                                {eyebrow}
                            </Text>
                        </MotionBox>
                    )}

                    <MotionBox variants={textFadeInVariantsNoTransition}>
                        <Title
                            order={2}
                            m={0}
                            c="gray.9"
                            fw={400}
                            lh="100%"
                            fz={{ base: 'var(--gm-refresh-title-sm)', sm: 'var(--gm-refresh-title-lg)' }}
                            lts={isSm ? -1.28 : -1.92}
                        >
                            {title}
                        </Title>
                    </MotionBox>

                    <MotionBox variants={textFadeInVariantsNoTransition}>
                        <Text m={0} fw={500} c="gray.6" fz={{ base: 16, sm: 20 }} lh="130%">
                            {description}
                        </Text>
                    </MotionBox>
                </Flex>

                <MotionBox variants={textFadeInVariantsNoTransition}>
                    <Flex
                        direction="column"
                        gap={24}
                        bg="#fff"
                        p={24}
                        h={{ base: 'auto', md: 583 }}
                        justify="space-between"
                        style={{ borderRadius: 24 }}
                    >
                        <Box
                            pos="relative"
                            w="100%"
                            h={{ base: 220, md: '50%' }}
                            mih={{ base: 220, md: 290 }}
                            style={{ borderRadius: 24, overflow: 'hidden' }}
                        >
                            <Box
                                component={motion.div}
                                variants={imageScaleVariants}
                                pos="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                            >
                                <Box
                                    component="img"
                                    src={post.image}
                                    alt={post.title}
                                    draggable={false}
                                    onDragStart={(e) => e.preventDefault()}
                                    w="100%"
                                    h="100%"
                                    style={{ objectFit: 'cover' }}
                                />
                            </Box>
                        </Box>

                        <Flex direction="column" w="100%">
                            <Title
                                order={3}
                                m={0}
                                fw={400}
                                maw={{ base: '100%', lg: '80%', xl: '50%' }}
                                c="gray.9"
                                lh="100%"
                                fz={{ base: 28, lg: 32 }}
                                lts={{ base: -1.12, md: -1.28 }}
                            >
                                {post.title}
                            </Title>
                            <Text m={0} mt={14} fw={400} c="gray.6" fz={{ base: 16, sm: 18 }} lh="130%">
                                {post.readingTime}
                            </Text>
                            <RefreshCtaButton
                                href={ctaHref ?? post.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="primary"
                                mt={32}
                                w="fit-content"
                            >
                                {ctaLabel}
                            </RefreshCtaButton>
                        </Flex>
                    </Flex>
                </MotionBox>
            </Flex>
        </Box>
    )
}
